import first from "lodash/first";
import last from "lodash/last";
import { type FunctionComponent, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { graphql, useFragment } from "react-relay";

import { checkIfNewsSectionInFirstIndex } from "~/lib/section";
import type { articleReactionArticle$key } from "~/queries/__generated__/articleReactionArticle.graphql";

import { useArticleReactionUtils } from "./hooks";
import type { ContentReactionProps } from "./styles";
import { StyledContentReaction } from "./styles";

export type Props = {
  className?: string;
  isEnabled?: boolean;
  reference: articleReactionArticle$key;
} & PropsFromStyledProps<ContentReactionProps>;

export const ArticleReaction: FunctionComponent<Props> = ({
  className,
  isEnabled = true,
  reference,
  size,
  variant,
}) => {
  const article = useFragment<articleReactionArticle$key>(
    graphql`
      fragment articleReactionArticle on Article {
        entityId
        sections {
          value {
            name
            ...helpersCheckIfNewsSectionInFirstIndexSection
          }
        }
        ...hooksArticleReactionUtilsArticle
      }
    `,
    reference,
  );

  const firstSection = useMemo(() => first(article.sections)?.value ?? [], [article.sections]);
  const sectionForGA4 = useMemo(
    () =>
      last(
        firstSection.filter((section, index) => !checkIfNewsSectionInFirstIndex(section, index)),
      ),
    [firstSection],
  );

  const { isEnabled: isEnabledInArticle } = useArticleReactionUtils(article);

  if (!isEnabled || !isEnabledInArticle) return null;

  return (
    <ErrorBoundary fallback={<></>}>
      <StyledContentReaction
        $size={size}
        $variant={variant}
        className={className}
        entityId={article.entityId}
        ga4CustomizedParameter={{
          article_id: article.entityId,
          page_name: "article",
          section_type: sectionForGA4?.name ?? "",
        }}
      />
    </ErrorBoundary>
  );
};

ArticleReaction.displayName = "ArticleReaction";
